import { MouseEvent, useCallback, useContext } from 'react'

import { userActivityEvents } from 'analytics/utils'
import { getCustomerApiClient } from 'api/CustomerApiClient'
import { toast } from 'components/ui/toaster'
import { useAnalytics } from 'context/AnalyticsContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import useDatadog from 'hooks/useDataDog'

import { FileItem } from '../../api/ApiTypes'
import AuthContext from '../../context/AuthContext'
import * as FileUtils from '../../utils/FileUtils'

/**
 * Hook to provide file access functionality to components and handle
 * the current origin and admin session state.
 */
const useFileAccess = (onDownloaded?: (fileId: string) => void) => {
  const { data: authData } = useContext(AuthContext)
  const { t } = useEcpTranslation()
  const datadogRum = useDatadog()
  const { analytics } = useAnalytics()

  const isAdminSession = authData?.isAdminSession

  const checkFileLink = useCallback(
    async (e: MouseEvent, fileId: string) => {
      e.stopPropagation()

      const target = (e.target as HTMLElement).closest('a')
      const href = target.href

      // Should never happen, but just in case
      if (href === '#') {
        const error = new Error('Unavailable')

        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error.message
          })
        })

        datadogRum?.addError(error, { action: 'download' })

        return
      }

      // Verify link expiration to prevent issues with expired links
      if (FileUtils.isSignedUrlExpired(href)) {
        const error = new Error('Expired')

        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error.message
          })
        })

        datadogRum?.addError(error, { action: 'download' })

        e.preventDefault()

        return
      }

      onDownloaded && onDownloaded(fileId)

      analytics?.addEvents([
        {
          type: userActivityEvents.documentDownloaded,
          details: {
            pagePath: window.location.pathname,
            documentId: fileId
          },
          timestamp: new Date().toISOString()
        }
      ])

      // Track the user has downloaded the file but only if it's not an admin
      // logged in as a customer
      if (!isAdminSession) {
        getCustomerApiClient().trackFileDownloaded({ id: fileId })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datadogRum, isAdminSession, onDownloaded, t]
  )

  const getBinaryContent = useCallback(
    (file: FileItem) => FileUtils.getBinaryContent({ file, isAdminSession }),
    [isAdminSession]
  )

  const downloadFile = useCallback(
    async (file: FileItem, { alreadyTracked = false } = {}) => {
      try {
        await FileUtils.openFile({ file, isAdminSession, alreadyTracked })

        onDownloaded?.(file._id)
        analytics?.addEvents([
          {
            type: userActivityEvents.documentDownloaded,
            details: {
              pagePath: window.location.pathname,
              documentId: file._id
            },
            timestamp: new Date().toISOString()
          }
        ])
      } catch (error) {
        toast.error({
          title: t('common.error.something_went_wrong'),
          description: t('common.error.download_failed', {
            error: error instanceof Error ? error.message : String(error)
          })
        })

        datadogRum?.addError(error, { action: 'download' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datadogRum, isAdminSession, onDownloaded, t]
  )

  const trackPreviewOpened = useCallback(
    (file: FileItem, { alreadyTracked }: { alreadyTracked: boolean }) => {
      analytics?.addEvents([
        {
          type: userActivityEvents.documentViewed,
          details: {
            pagePath: window.location.pathname,
            documentId: file._id
          },
          timestamp: new Date().toISOString()
        }
      ])

      if (alreadyTracked) return

      onDownloaded?.(file._id)

      if (!isAdminSession) {
        getCustomerApiClient().trackFileDownloaded({ id: file._id })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdminSession, onDownloaded]
  )

  const trackPreviewDownload = useCallback(
    (file: FileItem, { alreadyTracked }: { alreadyTracked: boolean }) => {
      analytics?.addEvents([
        {
          type: userActivityEvents.documentDownloaded,
          details: {
            pagePath: window.location.pathname,
            documentId: file._id
          },
          timestamp: new Date().toISOString()
        }
      ])

      if (alreadyTracked) return

      onDownloaded?.(file._id)

      if (!isAdminSession) {
        getCustomerApiClient().trackFileDownloaded({ id: file._id })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdminSession, onDownloaded]
  )

  const getPreviewDownloadUrl = useCallback(
    (file: FileItem, previewDownloadUrl?: string): string | undefined =>
      FileUtils.isTokenAuthFile(file)
        ? undefined
        : previewDownloadUrl ?? file.public_url,
    []
  )

  return {
    getBinaryContent,
    checkFileLink,
    downloadFile,
    getPreviewDownloadUrl,
    trackPreviewOpened,
    trackPreviewDownload
  }
}

export default useFileAccess
